import { useEffect, useState } from 'react'

import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle, Info, X } from 'lucide-react'

import { cn } from '@/lib/utils'

interface AnnouncementBarProps {
  message: string
  title?: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number // optional auto-dismiss
  onClose?: () => void
  action?: {
    label: string
    onClick: () => void
  }
  showIcon?: boolean
  dismissible?: boolean
}

export function AnnouncementBar({
  message,
  title,
  type = 'info',
  duration = 0, // 0 means dont auto-dismiss
  onClose,
  action,
  showIcon = false,
  dismissible = true,
}: AnnouncementBarProps) {
  const [visible, setVisible] = useState(true)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => handleClose(), duration)
      return () => clearTimeout(timer)
    }
  }, [duration])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      setVisible(false)
      onClose?.()
    }, 300)
  }

  if (!visible) return null

  const typeConfig = {
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-300',
      text: 'text-blue-900',
      icon: <Info className='h-4 w-4 text-blue-500' />,
    },
    success: {
      bg: 'bg-emerald-50',
      border: 'border-green-300',
      text: 'text-green-900',
      icon: <CheckCircle className='h-4 w-4 text-green-500' />,
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-300',
      text: 'text-yellow-900',
      icon: <AlertTriangle className='h-4 w-4 text-yellow-500' />,
    },
    error: {
      bg: 'bg-rose-100',
      border: 'border-red-300',
      text: 'text-red-700',
      icon: <AlertCircle className='h-4 w-4 text-rose-500' />,
    },
  }[type]

  return (
    <div
      className={cn(
        'relative flex w-full flex-col items-center justify-center gap-2 border-b px-4 py-2 transition-all duration-300 sm:flex-row sm:gap-3 sm:px-6 sm:py-1.5',
        typeConfig.bg,
        typeConfig.border,
        typeConfig.text,
        isExiting ? 'translate-y-[-100%] opacity-0' : 'translate-y-0 opacity-100'
      )}
    >
      <div className='flex w-full items-center justify-center gap-2 sm:w-auto sm:gap-3'>
        {showIcon && typeConfig.icon}
        <div className='flex flex-1 flex-col items-center text-center sm:flex-row sm:gap-2'>
          {title && <span className='text-xs font-semibold sm:text-sm'>{title}</span>}
          <span className='text-[0.7rem] leading-tight sm:text-[0.75rem]'>{message}</span>
        </div>
      </div>

      {/* Optional action */}
      {action && (
        <button
          className={cn(
            'flex shrink-0 cursor-pointer items-center gap-1 text-[0.7rem] font-semibold underline transition-colors duration-150 sm:ml-2 sm:text-xs',
            type === 'info' && 'text-blue-700 hover:text-blue-900',
            type === 'success' && 'text-green-700 hover:text-green-900',
            type === 'warning' && 'text-yellow-700 hover:text-yellow-900',
            type === 'error' && 'text-red-700 hover:text-red-900'
          )}
          onClick={action.onClick}
        >
          {action.label}
          <ArrowRight className='h-3 w-3' />
        </button>
      )}

      {/* Optional dismiss */}
      {dismissible && (
        <button
          className='absolute top-2 right-2 size-4 shrink-0 cursor-pointer text-gray-500 hover:bg-white/50 hover:text-gray-700 sm:right-4'
          onClick={handleClose}
          aria-label='Close announcement'
        >
          <X className='h-4 w-4' />
        </button>
      )}
    </div>
  )
}
